import { useState, useEffect } from 'react';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useUserRole } from './useUserRole';
import type { Horse } from '../types/horse';

export const useHorseAccess = () => {
  const { user } = useAuth();
  const { isManager } = useUserRole();
  const [horses, setHorses] = useState<Horse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;

    const q = query(collection(db, 'horses'));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const horsesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          coRiders: doc.data().coRiders || {}
        })) as Horse[];
        setHorses(horsesData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Error loading horses:', err);
        setError('Fehler beim Laden der Pferde');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  const canManageHorse = (horse: Horse) => {
    if (!user || !horse) return false;
    if (isManager) return true;
    if (horse.owner.id === user.uid) return true;
    return horse.coRiders?.[user.uid]?.status === 'approved';
  };

  const getAccessibleHorses = () => {
    if (!user) return [];
    if (isManager) return horses;
    return horses.filter(horse => 
      horse.owner.id === user.uid || 
      horse.coRiders?.[user.uid]?.status === 'approved'
    );
  };

  const getOwnedHorses = () => {
    if (!user) return [];
    return horses.filter(horse => horse.owner.id === user.uid);
  };

  const getCoRiderHorses = () => {
    if (!user) return [];
    return horses.filter(horse => 
      horse.coRiders?.[user.uid]?.status === 'approved'
    );
  };

  return {
    horses,
    loading,
    error,
    canManageHorse,
    getAccessibleHorses,
    getOwnedHorses,
    getCoRiderHorses
  };
};